import { reactive, SetupContext } from 'vue'

import mineTypes from '../utils/minetypes'
import STATUS from '../utils/status'

import useThumbnail from '../hooks/thumbnail'

import { UseConfig } from '@/components/filepicker/hooks/config'

interface UseItemManagerProps {
  config: UseConfig
  context: SetupContext
}

export interface Items {
  all: {
    [key: string]: {
      file: File
      thumbnail: string | ArrayBuffer | null
      upload: { progress: number }
      status: string
      accepted: boolean
      id: string
    }
  }
  ids: string[]
}

export interface ItemManager {
  getItems: () => Items
  removeFile: (id: string) => void
  addFile: (id: string, file: File) => void
}

export default function useItemManager({
  config,
  context,
}: UseItemManagerProps): { itemManager: ItemManager } {
  const items: Items = reactive({
    ids: [],
    all: {},
  })

  /**
   * callback for thumbnail generation
   *
   * @param itemId - item id
   * @param thumbnail - generated thumbnail
   */
  const thumbnailGenerated = (
    itemId: string,
    thumbnail: string | ArrayBuffer | null,
  ) => {
    if (items.all[itemId]) {
      items.all[itemId].thumbnail = thumbnail
    }
  }

  const { enqueueThumbnail } = useThumbnail(thumbnailGenerated)

  /**
   * Remove an uploaded file
   *
   * @param id - file id
   */
  const removeFile = (id: string) => {
    const index = items.ids.findIndex((idItem) => idItem === id)
    if (index < 0) {
      return
    }
    items.ids.splice(index, 1)
    const deletedFile = items.all[id]
    delete items.all[id]
    context.emit('removed-file', {
      file: deletedFile.file,
      status: deletedFile.status,
      id,
    })
  }
  /**
   *
   * Check if the file is an accepted
   *
   * @param file
   * @returns {boolean}
   */
  const isValidFile = (file: File) => {
    if (config.accepts === null || config.accepts.length === 0) {
      return true
    }
    const fileMineType = file.type
    let isValid =
      config.accepts.findIndex(
        (validType) => validType.trim() === fileMineType.trim(),
      ) !== -1
    if (!isValid) {
      // Retrieve the extension from the file if it exists
      const splitFileName = file.name.split('.')
      if (splitFileName.length > 1) {
        const ext = splitFileName[splitFileName.length - 1]
        // Retrieve mine-type from extension
        const extMineTypes = mineTypes.data
          .filter(
            (mt) =>
              mt.ext &&
              !!mt.ext.split(/\s/).find((extension) => ext === extension),
          )
          .map((mediaType) => mediaType.mime_type)
        isValid = !!config.accepts.find((mt) =>
          extMineTypes.find((extMt) => extMt === mt),
        )
      }
    }
    return isValid
  }
  const addFile = (id: string, file: File) => {
    if (config.maxFileSize && file.size > config.maxFileSize) {
      context.emit('error-add', { files: [file], error: 'MAX_FILE_SIZE' })
      return
    }
    // Filter invalid type
    if (!isValidFile(file)) {
      context.emit('error-add', { files: [file], error: 'INVALID_TYPE' })
      return
    }
    if (config.maxFiles && items.ids.length + 1 > config.maxFiles) {
      context.emit('error-add', { files: [file], error: 'MAX_FILE' })
      return
    }
    // Add the id
    items.ids.push(id)
    // eslint-disable-next-line no-param-reassign
    items.all[id] = {
      file,
      thumbnail: null,
      upload: { progress: 0 },
      status: STATUS.ADDED,
      accepted: true,
      id,
    }
    enqueueThumbnail(id, file)
    context.emit('added-file', { file: items.all[id].file, id })
  }

  const itemManager = {
    getItems: () => items,
    removeFile,
    addFile,
  }
  return { itemManager }
}
